import {
  type CreateReference,
  createCodeableConcept,
  createIdentifier,
  getFirstCodeFromCodableConcepts,
  IdentifierCode,
  isValidReference,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';

import type {
  Communication,
  CommunicationCategoryCode,
  CommunicationChannelCode,
  CommunicationContent,
  CommunicationCreateRequest,
  CommunicationTopicCode,
} from './schema';

export const mapCreateModelToFhirCommunication = (
  communication: CommunicationCreateRequest,
  mediaItemReference?: CreateReference,
): fhir4.Communication => {
  const {
    admission,
    category,
    channel,
    content,
    priority,
    recipient,
    sender,
    status,
    patient,
    topic,
    inResponseTo,
    messageId,
  } = communication;

  const payload = content.map((c) => ({
    contentString: c.content,
    contentReference: mediaItemReference,
  }));

  return {
    resourceType: 'Communication',
    subject: patient,
    encounter: admission,
    category: [createCodeableConcept(category)],
    medium: channel.map((c) => createCodeableConcept(c)),
    priority,
    sender,
    status: status,
    topic: createCodeableConcept(topic),
    payload,
    inResponseTo: inResponseTo ? [inResponseTo] : undefined,
    recipient: recipient ? [recipient] : undefined,
    identifier: messageId
      ? [createIdentifier(IdentifierCode.conversationMessage, messageId)]
      : undefined,
  };
};

export const mapModelToFhirCommunication = (
  communication: Communication,
): fhir4.Communication => {
  const {
    admission,
    category,
    channel,
    content,
    priority,
    recipient,
    sent,
    sender,
    status,
    patient,
    topic,
    inResponseTo,
    received,
  } = communication;

  const createFhirCommunication = mapCreateModelToFhirCommunication({
    admission,
    category,
    channel,
    content,
    priority,
    recipient,
    sender,
    status,
    patient,
    topic,
    inResponseTo,
  });

  return {
    ...createFhirCommunication,
    sent,
    received: received,
    recipient: recipient ? [recipient] : undefined,
  };
};

export const mapFhirCommunicationToModel = (
  communication: fhir4.Communication,
): Partial<Communication> => {
  const {
    subject,
    encounter,
    category,
    medium,
    priority,
    sender,
    sent,
    status,
    topic,
    payload,
    inResponseTo,
    received,
    recipient,
  } = communication;

  const firstRecipient = first(recipient);
  const firstInResponse = first(inResponseTo);

  const content = payload?.map((p) => {
    if (!p.contentString) {
      return undefined;
    }

    const contentResult: CommunicationContent = isValidReference(
      p.contentReference,
    )
      ? {
          content: p.contentString,
          attachment: p.contentReference,
        }
      : {
          content: p.contentString,
        };

    return contentResult;
  });

  const channel = medium?.map<CommunicationChannelCode | undefined>((m) => {
    return getFirstCodeFromCodableConcepts<CommunicationChannelCode>([m]);
  });

  return {
    admission: isValidReference(encounter) ? encounter : undefined,
    category:
      getFirstCodeFromCodableConcepts<CommunicationCategoryCode>(category),
    channel: compact(channel),
    priority,
    recipient: isValidReference(firstRecipient) ? firstRecipient : undefined,
    sent,
    sender: isValidReference(sender) ? sender : undefined,
    status,
    patient: isValidReference(subject) ? subject : undefined,
    inResponseTo: isValidReference(firstInResponse)
      ? firstInResponse
      : undefined,
    topic: topic
      ? getFirstCodeFromCodableConcepts<CommunicationTopicCode>([topic])
      : undefined,
    received,
    content: compact(content),
  };
};
